import {
    Box,
    Heading,
    Text,
    Card,
    CardBody,
    CardHeader,
    Flex,
    Badge,
    VStack,
    HStack,
    Spinner,
    Center,
    Button,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    useToast,
    Avatar,
    useColorModeValue,
} from '@chakra-ui/react';
import { MdPlayArrow, MdRefresh, MdAdd, MdCheck } from 'react-icons/md';
import { useAgencies, useRunAgency, useSubscriptions, useSubscribe, useUnsubscribe } from '@/services/hooks';

export function Agencies() {
    const toast = useToast();
    const { data: agencies, isLoading, refetch } = useAgencies({ refetchInterval: 5000 }); // Poll every 5s while page is open
    const runMutation = useRunAgency();

    const subColor = useColorModeValue('gray.600', 'gray.400');
    const regionColor = useColorModeValue('gray.500', 'gray.500'); // Neutral

    const { data: subscriptions, isLoading: subsLoading } = useSubscriptions();
    const subscribeMutation = useSubscribe();
    const unsubscribeMutation = useUnsubscribe();

    const handleRun = async (agencyId: number) => {
        try {
            await runMutation.mutateAsync(agencyId);
            toast({ title: 'Scraping started!', status: 'success' });
        } catch (e) {
            toast({ title: 'Failed to start', status: 'error' });
        }
    };

    const toggleSubscription = async (agencyId: number, isSubscribed: boolean) => {
        try {
            if (isSubscribed) {
                await unsubscribeMutation.mutateAsync(agencyId);
                toast({ title: 'Unsubscribed', status: 'info' });
            } else {
                await subscribeMutation.mutateAsync(agencyId);
                toast({ title: 'Subscribed!', status: 'success' });
            }
        } catch (e) {
            toast({ title: 'Error updating subscription', status: 'error' });
        }
    };

    if (isLoading || subsLoading) {
        return <Center py={20}><Spinner size="xl" /></Center>;
    }

    return (
        <Box>
            <Flex justify="space-between" align="center" mb={6}>
                <Heading size="lg">Agencies</Heading>
                <Button leftIcon={<MdRefresh />} onClick={() => refetch()} size="sm" isLoading={isLoading}>Refresh</Button>
            </Flex>

            <VStack spacing={6} align="stretch">
                {(agencies || []).map((agency: any) => (
                    <Card key={agency.id}>
                        <CardHeader pb={0}>
                            <Flex justify="space-between" align="center">
                                <HStack>
                                    <Avatar size="sm" src={agency.logo_url || undefined} name={agency.name.replace(/[^\\w\\s]/gi, '')} />
                                    <Box>
                                        <Heading size="md">{agency.name}</Heading>
                                        <Text fontSize="sm" color={regionColor}>{agency.region}</Text>
                                    </Box>
                                </HStack>
                                <HStack spacing={2}>
                                    <Button
                                        leftIcon={subscriptions?.some((s: any) => s.agency_id === agency.id) ? <MdCheck /> : <MdAdd />}
                                        variant={subscriptions?.some((s: any) => s.agency_id === agency.id) ? "outline" : "solid"}
                                        colorScheme={subscriptions?.some((s: any) => s.agency_id === agency.id) ? "green" : "brand"}
                                        size="sm"
                                        isLoading={(subscribeMutation.isPending || unsubscribeMutation.isPending) && (subscribeMutation.variables === agency.id || unsubscribeMutation.variables === agency.id)}
                                        onClick={() => toggleSubscription(agency.id, subscriptions?.some((s: any) => s.agency_id === agency.id))}
                                    >
                                        {subscriptions?.some((s: any) => s.agency_id === agency.id) ? "Subscribed" : "Subscribe"}
                                    </Button>
                                    <Button
                                        leftIcon={<MdPlayArrow />}
                                        colorScheme="gray"
                                        variant="ghost"
                                        size="sm"
                                        isLoading={runMutation.isPending && runMutation.variables === agency.id}
                                        onClick={() => handleRun(agency.id)}
                                    >
                                        Run Feeds
                                    </Button>
                                </HStack>
                            </Flex>
                        </CardHeader>
                        <CardBody>
                            <Table variant="simple" size="sm">
                                <Thead>
                                    <Tr>
                                        <Th>Feed Name</Th>
                                        <Th>Type</Th>
                                        <Th>Opportunity Type</Th>
                                        <Th>Last Run</Th>
                                        <Th>Status</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {agency.data_feeds.map((feed: any) => (
                                        <Tr key={feed.id}>
                                            <Td fontWeight="medium">{feed.name}</Td>
                                            <Td><Badge>{feed.feed_type}</Badge></Td>
                                            <Td>
                                                <Badge colorScheme={
                                                    feed.opportunity_type === 'active' ? 'green' :
                                                        feed.opportunity_type === 'future' ? 'blue' : 'gray'
                                                }>
                                                    {feed.opportunity_type}
                                                </Badge>
                                            </Td>
                                            <Td fontSize="sm" color={subColor}>
                                                {feed.last_run_at ? new Date(feed.last_run_at.endsWith('Z') ? feed.last_run_at : feed.last_run_at + 'Z').toLocaleString() : 'Never'}
                                            </Td>
                                            <Td>
                                                <Badge colorScheme={
                                                    feed.last_run_status === 'success' ? 'green' :
                                                        feed.last_run_status === 'failed' ? 'red' : 'yellow'
                                                }>
                                                    {feed.last_run_status === 'running' || feed.last_run_status === 'pending' ? (
                                                        <HStack>
                                                            <Spinner size="xs" />
                                                            <Text>Running...</Text>
                                                        </HStack>
                                                    ) : (
                                                        feed.last_run_status || 'Idle'
                                                    )}
                                                </Badge>
                                            </Td>
                                        </Tr>
                                    ))}
                                </Tbody>
                            </Table>
                        </CardBody>
                    </Card>
                ))}
            </VStack>
        </Box>
    );
}
